import React, { useState, useEffect, useCallback } from "react";
import { api, useBusiness, useAuth } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const formatCurrency = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v || 0);

const ReceiptVault = () => {
  const { selectedBusiness } = useBusiness();
  const { token } = useAuth();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [form, setForm] = useState({ vendor: "", amount: "", receipt_date: new Date().toISOString().split("T")[0], business_use: "", notes: "" });
  const [file, setFile] = useState(null);

  const fetchReceipts = useCallback(async () => {
    if (!selectedBusiness) return;
    setLoading(true);
    try {
      const res = await api.get(`/receipts/${selectedBusiness.id}`);
      setReceipts(res.data);
    } catch { toast.error("Failed to load receipts"); }
    finally { setLoading(false); }
  }, [selectedBusiness]);

  useEffect(() => { fetchReceipts(); }, [fetchReceipts]);

  const resetForm = () => { setForm({ vendor: "", amount: "", receipt_date: new Date().toISOString().split("T")[0], business_use: "", notes: "" }); setFile(null); setEditingReceipt(null); };

  const uploadReceipt = async () => {
    if (!file) { toast.error("Please select a file"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const params = new URLSearchParams({ business_id: selectedBusiness.id });
      if (form.vendor) params.append("vendor", form.vendor);
      if (form.amount) params.append("amount", form.amount);
      if (form.receipt_date) params.append("receipt_date", form.receipt_date);
      if (form.business_use) params.append("business_use", form.business_use);
      if (form.notes) params.append("notes", form.notes);
      await api.post(`/receipts?${params.toString()}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Receipt uploaded");
      setShowUpload(false); resetForm(); fetchReceipts();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to upload"); }
    finally { setUploading(false); }
  };

  const updateReceipt = async () => {
    try {
      const data = {};
      if (form.vendor) data.vendor = form.vendor;
      if (form.amount) data.amount = parseFloat(form.amount);
      if (form.receipt_date) data.receipt_date = form.receipt_date;
      if (form.business_use) data.business_use = form.business_use;
      if (form.notes) data.notes = form.notes;
      await api.put(`/receipts/${editingReceipt.id}`, data);
      toast.success("Receipt updated");
      setEditingReceipt(null); resetForm(); fetchReceipts();
    } catch { toast.error("Failed to update"); }
  };

  const deleteReceipt = async (id) => {
    try { await api.delete(`/receipts/${id}`); toast.success("Receipt deleted"); fetchReceipts(); }
    catch { toast.error("Failed to delete"); }
  };

  const openEdit = (r) => {
    setEditingReceipt(r);
    setForm({ vendor: r.vendor || "", amount: r.amount?.toString() || "", receipt_date: r.receipt_date || "", business_use: r.business_use || "", notes: r.notes || "" });
  };

  const totalAmount = receipts.reduce((s, r) => s + (r.amount || 0), 0);
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  if (!selectedBusiness) return <div className="flex items-center justify-center min-h-[400px]"><p className="text-muted-foreground">Please select a business.</p></div>;
  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-pulse text-muted-foreground">Loading...</div></div>;

  return (
    <div className="space-y-6" data-testid="receipt-vault-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-stone-900">Receipt Vault</h1>
          <p className="text-stone-600">Store and organize receipts for tax documentation</p>
        </div>
        <Button onClick={() => { resetForm(); setShowUpload(true); }} className="bg-[#1e3a5f] hover:bg-[#2d5a8e]" data-testid="upload-receipt-btn">+ Upload Receipt</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card><CardContent className="pt-5 pb-4 text-center">
          <p className="text-2xl font-bold text-stone-800">{receipts.length}</p>
          <p className="text-xs text-stone-500 mt-1">Total Receipts</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{formatCurrency(totalAmount)}</p>
          <p className="text-xs text-stone-500 mt-1">Total Amount</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4 text-center">
          <p className="text-2xl font-bold text-[#1e3a5f]">{receipts.filter(r => r.business_use).length}</p>
          <p className="text-xs text-stone-500 mt-1">Categorized</p>
        </CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg">All Receipts</CardTitle></CardHeader>
        <CardContent>
          {receipts.length === 0 ? (
            <div className="text-center py-8">
              <svg className="w-12 h-12 mx-auto text-stone-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1"><path strokeLinecap="round" strokeLinejoin="round" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              <p className="text-muted-foreground">No receipts uploaded yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {receipts.map(r => (
                <div key={r.id} className="p-3 rounded-lg border border-stone-200 hover:border-stone-300 transition-colors" data-testid={`receipt-${r.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-medium text-sm text-stone-900">{r.vendor || r.file_name}</p>
                      {r.amount && <p className="text-lg font-bold text-stone-800">{formatCurrency(r.amount)}</p>}
                    </div>
                    <div className="flex gap-1">
                      {r.file_url && (
                        <a href={`${API_BASE}/api/files/${r.file_url}?auth=${token}`} target="_blank" rel="noopener noreferrer" className="p-1 rounded hover:bg-stone-100" title="View">
                          <svg className="w-4 h-4 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                        </a>
                      )}
                      <button onClick={() => openEdit(r)} className="p-1 rounded hover:bg-stone-100"><svg className="w-4 h-4 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" /></svg></button>
                      <button onClick={() => deleteReceipt(r.id)} className="p-1 rounded hover:bg-red-50"><svg className="w-4 h-4 text-stone-400 hover:text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg></button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 text-xs text-stone-500">
                    {r.receipt_date && <span>{r.receipt_date}</span>}
                    {r.business_use && <Badge variant="outline" className="text-[10px]">{r.business_use}</Badge>}
                    {r.content_type?.includes("pdf") && <Badge variant="outline" className="text-[10px] text-red-600 border-red-200">PDF</Badge>}
                    {r.content_type?.includes("image") && <Badge variant="outline" className="text-[10px] text-blue-600 border-blue-200">Image</Badge>}
                  </div>
                  {r.notes && <p className="text-xs text-stone-500 mt-1 italic">{r.notes}</p>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Receipt</DialogTitle>
            <DialogDescription>Upload a receipt image or PDF</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>File *</Label>
              <Input type="file" accept="image/*,.pdf" onChange={e => setFile(e.target.files?.[0])} data-testid="receipt-file-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Vendor</Label><Input placeholder="Store name" value={form.vendor} onChange={e => setForm({...form, vendor: e.target.value})} data-testid="receipt-vendor" /></div>
              <div className="space-y-2"><Label>Amount</Label><Input type="number" step="0.01" placeholder="0.00" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} data-testid="receipt-amount" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Date</Label><Input type="date" value={form.receipt_date} onChange={e => setForm({...form, receipt_date: e.target.value})} data-testid="receipt-date" /></div>
              <div className="space-y-2"><Label>Business Use</Label><Input placeholder="e.g., Office supplies" value={form.business_use} onChange={e => setForm({...form, business_use: e.target.value})} data-testid="receipt-use" /></div>
            </div>
            <div className="space-y-2"><Label>Notes</Label><Textarea placeholder="Optional notes" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} data-testid="receipt-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
            <Button onClick={uploadReceipt} disabled={uploading} className="bg-[#1e3a5f] hover:bg-[#2d5a8e]" data-testid="save-receipt-btn">{uploading ? "Uploading..." : "Upload"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingReceipt} onOpenChange={v => !v && setEditingReceipt(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Receipt</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Vendor</Label><Input value={form.vendor} onChange={e => setForm({...form, vendor: e.target.value})} /></div>
              <div className="space-y-2"><Label>Amount</Label><Input type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Date</Label><Input type="date" value={form.receipt_date} onChange={e => setForm({...form, receipt_date: e.target.value})} /></div>
              <div className="space-y-2"><Label>Business Use</Label><Input value={form.business_use} onChange={e => setForm({...form, business_use: e.target.value})} /></div>
            </div>
            <div className="space-y-2"><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingReceipt(null)}>Cancel</Button>
            <Button onClick={updateReceipt} className="bg-[#1e3a5f] hover:bg-[#2d5a8e]">Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReceiptVault;
